import React, { useEffect, useState, useRef } from "react"; // ACCESSIBILITY: added useRef for focus management
import { IListObj } from "../types/genericTypes";
import "../../assets/scss/generic/tocDropdown.scss";

export interface ITocDropdownProps {
  selectedValue?: number | string | boolean; // default selected value
  dropdownOptions: Array<IListObj> | Array<any>; // list of all options in dropdown
  onSelect: (id: string | number | boolean) => void; // the function to be called onSelect
  showPrimary?: boolean; // true if need to show star, otherwise false
  showClose?: boolean; // true if need to show close button, otherwise false
  disabled?: boolean; //disable the dropdown
  isSearchable: boolean; // for searchable dropdown list
  // ACCESSIBILITY: added id and aria-label props so parent can associate
  // a <label> with this dropdown and provide context to screen readers
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const TocDropdown: React.FC<ITocDropdownProps> = (props: ITocDropdownProps) => {
  /**
   * state and function for isDropdownOpen
   * selectedValue type is boolean
   * setSelectedValue(boolean)
   */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropDownOption, setDropDownOption] = useState<Array<IListObj>>();
  const [search, setSearch] = useState<any>();

  // ACCESSIBILITY: tracks keyboard-highlighted option index so
  // aria-activedescendant can point screen readers to the focused option
  const [activeIndex, setActiveIndex] = useState(-1);

  // ACCESSIBILITY: ref to the dropdown list so we can scroll the
  // highlighted option into view during keyboard navigation
  const listRef = useRef<HTMLUListElement>(null);

  // ACCESSIBILITY: ref to the trigger button/input so we can return
  // focus to it when the dropdown closes via keyboard
  const triggerRef = useRef<HTMLButtonElement & HTMLInputElement>(null);

  // ACCESSIBILITY: stable unique id for the listbox so aria-controls
  // and aria-owns can reference it
  const dropdownId = props.id ? `${props.id}-listbox` : "toc-dropdown-listbox";

  // ACCESSIBILITY: id for the currently active option, used by
  // aria-activedescendant to tell screen readers which option is focused
  const activeOptionId =
    activeIndex >= 0 ? `${dropdownId}-option-${activeIndex}` : undefined;

  /**
   * called when user selects an option
   * @param id id of the selected option
   * @returns onSelect prop passed from parent
   */
  function optionSelected(id: string | number | boolean) {
    setIsDropdownOpen(false);
    setActiveIndex(-1);
    // ACCESSIBILITY: return focus to trigger after selection so keyboard
    // users don't lose their place in the page
    triggerRef.current?.focus();
    return props.onSelect(id);
  }

  function handleChange(e: any) {
    setSearch(e.target.value);
    const list = props.dropdownOptions.filter((opt) => {
      if (opt.display.toLowerCase().includes(e.target.value.toLowerCase()))
        return opt;
    });
    setIsDropdownOpen(true);
    setActiveIndex(-1);
    setDropDownOption(list);
  }

  // ACCESSIBILITY: keyboard navigation handler — Arrow keys move highlight,
  // Enter selects, Escape closes. Required by WCAG 2.1 SC 1.3.1 and the
  // ARIA listbox keyboard interaction pattern
  function handleKeyDown(e: React.KeyboardEvent) {
    if (!dropDownOption || dropDownOption.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault(); // prevent page scroll
      if (!isDropdownOpen) {
        setIsDropdownOpen(true);
        return;
      }
      const next = Math.min(activeIndex + 1, dropDownOption.length - 1);
      setActiveIndex(next);
      scrollOptionIntoView(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); // prevent page scroll
      const prev = Math.max(activeIndex - 1, 0);
      setActiveIndex(prev);
      scrollOptionIntoView(prev);
    } else if (e.key === "Enter" && activeIndex >= 0 && isDropdownOpen) {
      e.preventDefault(); // prevent form submission
      optionSelected(dropDownOption[activeIndex].value);
    } else if (e.key === "Escape") {
      // ACCESSIBILITY: Escape closes dropdown and returns focus to trigger
      // per the ARIA listbox keyboard interaction pattern
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      triggerRef.current?.focus();
    } else if (e.key === " " && !props.isSearchable) {
      // ACCESSIBILITY: Space opens the dropdown for button-style triggers
      e.preventDefault();
      setIsDropdownOpen((prev) => !prev);
    }
  }

  // ACCESSIBILITY: scrolls the highlighted option into view inside the listbox
  // so keyboard users don't have to manually scroll to see the focused item
  function scrollOptionIntoView(index: number) {
    const list = listRef.current;
    if (!list) return;
    const item = list.querySelectorAll('[role="option"]')[index] as HTMLElement;
    item?.scrollIntoView({ block: "nearest" });
  }

  // Find the selected object
  const selectedOption = props.dropdownOptions.find(
    (option) => props.selectedValue === option.value
  );

  useEffect(() => {
    const selectedOption = props.dropdownOptions.find(
      (option) => props.selectedValue === option.value
    );
    setSearch(selectedOption?.display);
    const options = props.dropdownOptions.filter(
      (opt) => props.selectedValue !== opt.value
    );
    setDropDownOption(options);
  }, [props.selectedValue, props.dropdownOptions]);

  return (
    <div
      className={`dropdown_wrap ${props.disabled ? 'disabled' : ''}`}
      // ACCESSIBILITY: onKeyDown at wrapper level catches keyboard events
      // from both the searchable input and the button trigger
      onKeyDown={handleKeyDown}
    >
      {props.isSearchable ? (
        <>
          <input
            // ACCESSIBILITY: use props.id so parent <label htmlFor> works.
            // Fallback to original id if no id prop passed
            id={props.id || "tableDataSearchEle"}
            ref={triggerRef as React.RefObject<HTMLInputElement>}
            autoFocus
            type="text"
            className="dropdown_button"
            onChange={handleChange}
            value={search ? search : ""}
            placeholder="Search"
            autoComplete="off"
            disabled={props.disabled}
            // ACCESSIBILITY: combobox role + aria attributes tell screen
            // readers this input controls a listbox dropdown
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isDropdownOpen}
            aria-controls={dropdownId}
            aria-activedescendant={activeOptionId}
            aria-label={props["aria-label"]}
            aria-labelledby={props["aria-labelledby"]}
            aria-disabled={props.disabled}
          />
          {/* ACCESSIBILITY: changed from <i onClick> to <button> so keyboard
              users can open the dropdown via the arrow icon */}
          <button
            type="button"
            aria-label="Open dropdown"
            aria-hidden={props.disabled}
            tabIndex={props.disabled ? -1 : 0}
            onClick={() => !props.disabled && setIsDropdownOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <i className="edel-icon-arrow-down" aria-hidden="true" />
          </button>
        </>
      ) : (
        <button
          id={props.id}
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          className="dropdown_button"
          onClick={(e) => {
            e.preventDefault();
            if (props.disabled) return;
            !(e as any).target.className.includes("edel-icon-close") &&
              setIsDropdownOpen((prev) => !prev);
          }}
          disabled={props.disabled}
          type="button"
          // ACCESSIBILITY: aria attributes turn the button into a proper
          // listbox trigger that screen readers understand
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          aria-controls={dropdownId}
          aria-activedescendant={activeOptionId}
          aria-label={props["aria-label"]}
          aria-labelledby={props["aria-labelledby"]}
        >
          {/* Show placeholder if no option is selected */}
          <span className={selectedOption ? "selected" : "placeholder"}>
            {selectedOption?.display || "Select an option"}
            {/* Show star only if primary is to be shown and first option is selected */}
            {props.showPrimary && selectedOption?.primary && (
              // ACCESSIBILITY: aria-hidden hides decorative star from screen readers
              <i className="star edel-icon-star" aria-hidden="true" />
            )}
          </span>
          {props.showClose && props.selectedValue && !props.disabled ? (
            // ACCESSIBILITY: close icon needs its own accessible label so screen
            // readers don't just announce "button" with no context
            <i
              className="edel-icon-close"
              role="button"
              aria-label="Clear selection"
              tabIndex={0}
              onClick={() => optionSelected("")}
              onKeyDown={(e) => e.key === "Enter" && optionSelected("")}
            />
          ) : (
            // ACCESSIBILITY: decorative arrow hidden from screen readers —
            // the button's aria-expanded already communicates open/closed state
            <i className="edel-icon-arrow-down" aria-hidden="true" />
          )}
        </button>
      )}

      {/* when dropdown is open show overlay and content */}
      {isDropdownOpen && !props.disabled && (
        <>
          {/* Overlay covers the whole screen. When overlay is clicked, close the dropdown. */}
          <div
            className="dropdown_overlay"
            onClick={() => setIsDropdownOpen(false)}
          />

          {/* ACCESSIBILITY: role="listbox" makes this a proper ARIA listbox.
              aria-label gives it a name for screen readers.
              ref enables scroll-into-view during keyboard navigation */}
          <ul
            id={dropdownId}
            ref={listRef}
            className="dropdown_content"
            role="listbox"
            aria-label={
              props["aria-label"] ||
              (selectedOption ? `${selectedOption.display} options` : "Options")
            }
          >
            {dropDownOption &&
              dropDownOption.length > 0 &&
              dropDownOption.map((option, index) => (
                <li
                  // ACCESSIBILITY: unique id per option so aria-activedescendant
                  // on the trigger can point to the keyboard-focused option
                  id={`${dropdownId}-option-${index}`}
                  className={
                    "dropdown_item" +
                    (option.value === props.selectedValue ? " active" : "") +
                    (activeIndex === index ? " keyboard_active" : "")
                  }
                  key={index}
                  // ACCESSIBILITY: role="option" + aria-selected tells screen
                  // readers this is a selectable listbox item and its state.
                  // aria-selected reflects both keyboard highlight and selection
                  role="option"
                  aria-selected={
                    activeIndex === index || option.value === props.selectedValue
                  }
                  onClick={() => optionSelected(option.value)}
                >
                  <div className="dropdown_item_inner">
                    <div>
                      {/* show tick only if this option is selected */}
                      <span className="tick_container">
                        {option.value === props.selectedValue && (
                          // ACCESSIBILITY: aria-hidden hides decorative tick —
                          // aria-selected already communicates selection state
                          <i className="tick edel-icon-tick" aria-hidden="true" />
                        )}
                      </span>
                      {/* ACCESSIBILITY: changed from <label> to <span> —
                          <label> inside a listbox item is invalid HTML and
                          causes screen readers to announce it incorrectly.
                          The option text is already read via role="option" */}
                      <span>{option.display}</span>
                    </div>
                    {option.primary && props.showPrimary && (
                      <div>
                        {/* ACCESSIBILITY: decorative star hidden from screen readers */}
                        <i className="star edel-icon-star" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default TocDropdown;